import type {ComponentProps} from 'react';
import {BrowserRouter as Router, Switch, Route} from 'react-router-dom';
import {ApolloProvider, apolloClient} from 'services/apollo';
import {FlashMessagesProvider} from '@ezcater/flash-messages';
import FlashMessage from 'components/FlashMessage';
import {
  PROGRESS_PATH,
  HELP_PATH,
  ORDER_HELP_PATH,
  DELIVERIES_PATH,
  COMPLETED_DELIVERIES_PATH,
  EXPIRED_MAGIC_LINK_PATH,
  REQUEST_SIGN_IN_LINK_PATH,
  SELF_ASSIGN_PATH,
  SELF_ASSIGN_REGISTRATION_PATH,
} from 'paths';
import {
  DriverProgress,
  MyDeliveries,
  Help,
  CompletedDeliveries,
  SelfAssign,
  SelfAssignRegistration,
  ExpiredMagicLink,
  RequestSignInLink,
} from 'pages';
import CurrentAccountProviderWithQuery from 'components/Layout/CurrentAccountProviderWithQuery';
import {legacyApolloClient} from './services/apollo/apolloClient';
import {useFederationApiEppoFlag} from './services/eppo';
import SessionExpiryHandler from './components/SessionExpiryHandler';

type AuthenticateRouteProps = ComponentProps<typeof Route> & {
  readonly redirect?: boolean;
  readonly skipComplianceModal?: boolean;
};

const AuthenticatedRoute = ({
  redirect = true,
  skipComplianceModal,
  ...props
}: AuthenticateRouteProps) => {
  return (
    <CurrentAccountProviderWithQuery redirect={redirect} skipComplianceModal={skipComplianceModal}>
      <Route {...props} />
    </CurrentAccountProviderWithQuery>
  );
};

type DynamicApolloProviderProps = {
  children: React.ReactNode;
};

const DynamicApolloProvider = ({children}: DynamicApolloProviderProps) => {
  const featureFlag = useFederationApiEppoFlag();

  if (featureFlag) {
    return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
  }

  return <ApolloProvider client={legacyApolloClient}>{children}</ApolloProvider>;
};

const Routes = () => {
  return (
    <Router>
      <SessionExpiryHandler />
      <FlashMessagesProvider FlashMessageTemplate={FlashMessage as unknown as React.ReactNode}>
        <DynamicApolloProvider>
          <Switch>
            <Route exact path={REQUEST_SIGN_IN_LINK_PATH} component={RequestSignInLink} />
            <AuthenticatedRoute
              exact
              path={[HELP_PATH, ORDER_HELP_PATH]}
              component={Help}
              redirect={false}
              skipComplianceModal
            />
            <Route exact path={EXPIRED_MAGIC_LINK_PATH} component={ExpiredMagicLink} />
            <AuthenticatedRoute
              exact
              path={SELF_ASSIGN_PATH}
              component={SelfAssign}
              redirect={false}
            />
            <AuthenticatedRoute
              exact
              path={SELF_ASSIGN_REGISTRATION_PATH}
              component={SelfAssignRegistration}
            />
            <AuthenticatedRoute exact path={[DELIVERIES_PATH, '/']} component={MyDeliveries} />
            <AuthenticatedRoute
              exact
              path={COMPLETED_DELIVERIES_PATH}
              component={CompletedDeliveries}
            />
            <AuthenticatedRoute exact path={PROGRESS_PATH} component={DriverProgress} />
          </Switch>
        </DynamicApolloProvider>
      </FlashMessagesProvider>
    </Router>
  );
};

export default Routes;
